import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { GetBillTosApiParameter } from "@insite/client-framework/Services/CustomersService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { BillToModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickableLinkProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SelectCustomerModal from "@WhiteCap/Components/SelectCustomerModal";
import SelectedBilltoCustomer from "@WhiteCap/Components/SelectedBilltoCustomer";
import SelectShipToModal from "@WhiteCap/Components/SelectShipToModal";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    noShipToAndCantCreate: boolean;
    setParameter: (parameter: GetBillTosApiParameter) => void;
    parameter: GetBillTosApiParameter;
    billTo?: BillToModel;
    onSelect: (billTo: BillToModel) => void;
    extendedStyles?: ChangeCustomerBillToSelectorStyles;
    isLoading?: boolean;
    totalItemCount?: number;
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    return {
        session: state.context.session,
        billTosById: state.data.billTos.byId,
    };
};

const mapDispatchToProps = {};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ChangeCustomerBillToSelectorStyles {
    changeButton?: ButtonPresentationProps;
    headerText?: TypographyPresentationProps;
    container?: GridContainerProps;
    selectedBillToGridItem?: GridItemProps;
    changeShipToGridItem?: GridItemProps;
    titleGridItem?: GridItemProps;
    switchAccountGridItem?: GridItemProps;
    switchAccountLink?: ClickableLinkProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
}

export const changeCustomerBillToSelectorStyles: ChangeCustomerBillToSelectorStyles = {
    changeButton: {
        variant: "secondary",
        buttonType: "outline",
        typographyProps: {
            css: css`
                font-size: 14px;
                font-weight: 450;
                line-height: 24px;
                text-transform: uppercase;
            `,
        },
        css: css`
            width: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        min-width: max-content;
                    `,
                    css`
                        min-width: max-content;
                    `,
                    css`
                        min-width: max-content;
                    `,
                    css`
                        min-width: max-content;
                    `,
                    css``,
                ])}
        `,
    },
    headerText: {
        weight: 600,
        css: css`
            margin-bottom: 8px;
        `,
    },
    container: {
        css: css`
            align-items: center;
            > div[class^="GridOffset"] {
                padding: 16px 24px;
            }
        `,
    },
    selectedBillToGridItem: {
        width: 12,
        css: css`
            padding: 0px;
            background-color: #f7f8f9;
            height: 140px;
            align-items: center;
        `,
    },
    changeShipToGridItem: {
        width: 12,
        css: css`
            padding: 8px 0px 0px 0px;
        `,
    },
    titleGridItem: {
        width: 12,
        css: css`
            padding: 0px;
        `,
    },
    switchAccountGridItem: {
        width: 12,
        css: css`
            justify-content: center;
        `,
    },
    switchAccountLink: {
        css: css`
            color: ${getColor("text.link")};
            justify-content: center;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 200px;
            display: flex;
            align-items: center;
            width: 100%;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
};

const ChangeCustomerBillToSelector: FC<Props> = ({
    parameter,
    onSelect,
    extendedStyles,
    session,
    billTo,
    totalItemCount,
    billTosById,
    isLoading,
}) => {
    const [styles] = useState(() => mergeToNew(changeCustomerBillToSelectorStyles, extendedStyles));
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [modalKey, setModalKey] = React.useState(0);
    const [shipToModalKey, setShipToModalKey] = useState(0);
    const [isShipToModalOpen, setIsShipToModalOpen] = useState(false);

    const handleChangeCustomerClick = () => {
        setModalKey(prev => prev + 1);
        setIsModalOpen(true);
    };

    const handleChangeShipToClick = () => {
        setShipToModalKey(prev => prev + 1);
        setIsShipToModalOpen(true);
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
    };

    const billToId = session.billToId ?? "";
    const currentBillToDetails = billTosById[billToId];

    const updatedTotalItemCount = (totalItemCount ?? 0) + 1;
  
    if (isLoading) {
        <StyledWrapper {...styles.centeringWrapper}>
            <LoadingSpinner {...styles.spinner} />
        </StyledWrapper>;
    }

    return (
        <>
            <GridContainer {...styles.container}>
                <GridItem {...styles.titleGridItem}>
                    <Typography as="p" {...styles.headerText}>
                        {translate("Account & Ship-To Address")}
                    </Typography>
                </GridItem>
                <GridItem {...styles.selectedBillToGridItem}>
                    {session?.isAuthenticated && !currentBillToDetails ? (
                        <StyledWrapper {...styles.centeringWrapper}>
                            <LoadingSpinner {...styles.spinner} />
                        </StyledWrapper>
                    ) : (
                        <SelectedBilltoCustomer selectedBillTo={currentBillToDetails} />
                    )}
                </GridItem>
                {!session?.isGuest && session?.isAuthenticated && (
                    <GridItem {...styles.changeShipToGridItem}>
                        <Button
                            onClick={handleChangeShipToClick}
                            data-test-selector="changeCustomerBillToSelector"
                            disabled={session?.isGuest}
                            {...styles.changeButton}
                        >
                            {translate("Change Ship-To Address")}
                        </Button>
                    </GridItem>
                )}
                {updatedTotalItemCount && updatedTotalItemCount > 1 && (
                    <GridItem {...styles.switchAccountGridItem}>
                        <Clickable onClick={handleChangeCustomerClick} {...styles.switchAccountLink}>
                            {`${translate("Switch Account")} (${updatedTotalItemCount})`}
                        </Clickable>
                    </GridItem>
                )}
            </GridContainer>
            <SelectCustomerModal
                modalIsOpen={isModalOpen}
                key={`customer-${modalKey}`}
                onModalClose={handleModalClose}
                billToParameter={parameter}
                onSelect={onSelect}
            />
            <SelectShipToModal
                modalIsOpen={isShipToModalOpen}
                key={`shipto-${shipToModalKey}`}
                onModalClose={() => {
                    setIsShipToModalOpen(false);
                }}
                billToParameter={{
                    ...parameter,
                    customerNumber: currentBillToDetails?.customerNumber ?? "",
                }}
                onSelect={onSelect}
                billTo={billTo}
                showBackButton={false}
            />
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ChangeCustomerBillToSelector);
